import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, OperatorPeriodKind, OperatorPeriodSummary } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";

const KINDS: { id: OperatorPeriodKind; label: string }[] = [
  { id: "day", label: "Dzień" },
  { id: "week", label: "Tydzień" },
  { id: "month", label: "Miesiąc" },
];

function formatGeneratedAt(iso: string): string {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso.slice(0, 16);
    return d.toLocaleString("pl-PL", {
      timeZone: "Europe/Warsaw",
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return iso.slice(0, 16);
  }
}

function DomainChips({ stats }: { stats?: Record<string, unknown> }) {
  const byDomain = (stats?.by_domain || {}) as Record<string, number>;
  const entries = Object.entries(byDomain).sort((a, b) => b[1] - a[1]);
  if (!entries.length) {
    return (
      <p className="text-sm text-muted-foreground">Brak interakcji w tym okresie.</p>
    );
  }
  return (
    <div className="flex flex-wrap gap-2">
      {entries.map(([dom, n]) => (
        <span
          key={dom}
          className="rounded-md border border-border/70 bg-muted/40 px-2.5 py-1 text-xs font-medium"
        >
          {dom}: {n}
        </span>
      ))}
      {typeof stats?.count === "number" ? (
        <span className="rounded-md border border-border/70 px-2.5 py-1 text-xs text-muted-foreground">
          razem {stats.count}
        </span>
      ) : null}
    </div>
  );
}

export default function Interakcje() {
  const [kind, setKind] = useState<OperatorPeriodKind>("day");
  const [periodKey, setPeriodKey] = useState<string>("");

  const currentQ = useQuery({
    queryKey: ["operator-summaries-current"],
    queryFn: () => api.operatorInteractionSummariesCurrent(),
  });

  const listQ = useQuery({
    queryKey: ["operator-summaries-list", kind],
    queryFn: () => api.operatorInteractionSummariesList(kind),
  });

  const defaultKey = useMemo(() => {
    const keys = currentQ.data?.keys;
    return keys?.[kind] || "";
  }, [currentQ.data, kind]);

  useEffect(() => {
    if (!periodKey && defaultKey) setPeriodKey(defaultKey);
  }, [defaultKey, periodKey]);

  useEffect(() => {
    setPeriodKey(defaultKey || "");
  }, [kind, defaultKey]);

  const detailQ = useQuery({
    queryKey: ["operator-summary", kind, periodKey],
    queryFn: () => api.operatorInteractionSummary(kind, periodKey),
    enabled: Boolean(periodKey),
    retry: false,
  });

  const pager: OperatorPeriodSummary | null = (() => {
    if (detailQ.data) return detailQ.data;
    const cur = currentQ.data?.[kind];
    if (cur && periodKey && cur.period_key === periodKey) return cur;
    return null;
  })();

  const history = listQ.data?.items || [];

  return (
    <div>
      <PageHeader
        title="Interakcje"
        subtitle="1-pagery dnia, tygodnia i miesiąca. Odświeżane raz dziennie po północy."
      />

      <div className="mb-4 flex flex-wrap gap-2">
        {KINDS.map((k) => (
          <Button
            key={k.id}
            size="sm"
            variant={kind === k.id ? "default" : "outline"}
            onClick={() => setKind(k.id)}
          >
            {k.label}
          </Button>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <label className="text-sm text-muted-foreground" htmlFor="period-select">
          Okres
        </label>
        <select
          id="period-select"
          className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          value={periodKey}
          onChange={(e) => setPeriodKey(e.target.value)}
        >
          {!periodKey ? <option value="">(wybierz)</option> : null}
          {history.map((row) => (
            <option key={`${row.kind}-${row.period_key}`} value={row.period_key}>
              {row.period_key}
              {row.title ? ` · ${row.title}` : ""}
            </option>
          ))}
          {periodKey && !history.some((r) => r.period_key === periodKey) ? (
            <option value={periodKey}>{periodKey}</option>
          ) : null}
        </select>
      </div>

      {detailQ.isLoading || currentQ.isLoading ? (
        <p className="text-sm text-muted-foreground">Ładuję 1-pager…</p>
      ) : null}

      {detailQ.isError && !pager ? (
        <Card>
          <CardContent className="py-8 text-sm text-muted-foreground">
            Brak 1-pagera dla tego okresu. Pojawi się po nocnej regeneracji (00:10)
            albo po pierwszym wpisie do rejestru.
          </CardContent>
        </Card>
      ) : null}

      {pager ? (
        <Card className={cn("max-w-3xl")}>
          <CardHeader className="space-y-3">
            <CardTitle className="text-xl">{pager.title || periodKey}</CardTitle>
            <p className="text-xs text-muted-foreground">
              Wygenerowano: {formatGeneratedAt(pager.generated_at)} · źródło:{" "}
              {pager.source}
            </p>
            <DomainChips stats={pager.stats} />
          </CardHeader>
          <CardContent>
            <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-foreground">
              {pager.body_md || "(pusty 1-pager)"}
            </pre>
          </CardContent>
        </Card>
      ) : null}

      <div className="mt-8 max-w-3xl">
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Ostatnie interakcje (14 dni)
        </h2>
        <RecentInteractions />
      </div>
    </div>
  );
}

function RecentInteractions() {
  const q = useQuery({
    queryKey: ["operator-interactions-recent"],
    queryFn: () => api.operatorInteractionsList({ days: 14, limit: 40 }),
  });
  const items = q.data?.items || [];
  if (q.isLoading) {
    return <p className="text-sm text-muted-foreground">Ładuję listę…</p>;
  }
  if (!items.length) {
    return (
      <p className="text-sm text-muted-foreground">
        Rejestr jeszcze pusty. Wpisy pojawią się po akcjach w panelu (Kanban,
        dokumenty, publikacje…).
      </p>
    );
  }
  return (
    <ul className="space-y-2 text-sm">
      {items.map((it) => (
        <li
          key={it.id}
          className="rounded-md border border-border/60 bg-card/40 px-3 py-2"
        >
          <div className="flex flex-wrap gap-x-2 gap-y-0.5">
            <span className="font-medium">
              [{it.domain}/{it.action}]
            </span>
            <span>{it.title}</span>
          </div>
          <div className="text-xs text-muted-foreground">
            {(it.attrs?.local_date as string) || it.ts?.slice(0, 10)} · {it.actor} ·{" "}
            {it.surface}
          </div>
        </li>
      ))}
    </ul>
  );
}
